import { lazy } from 'react';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import AuthGuard, { ZORUNLU_PAROLA_DEGISTIR_PATH } from 'utils/route-guard/AuthGuard';
import RoleGuard from 'utils/route-guard/RoleGuard';
import { routableItems } from 'menu-items/config';
import { RAPOR_KATALOGU } from 'api/raporlar';
import { YONETICI, BINA_SAKINI } from 'utils/roles';

const ZorunluParolaDegistirPage = Loadable(lazy(() => import('pages/auth/ZorunluParolaDegistir')));
const DashboardPage = Loadable(lazy(() => import('pages/dashboard')));
const BildirimlerPage = Loadable(lazy(() => import('pages/bildirimler')));
const NotesPage = Loadable(lazy(() => import('pages/notes')));
const DaireListesiPage = Loadable(lazy(() => import('pages/apartman/daire-listesi')));
const DaireDetayPage = Loadable(lazy(() => import('pages/apartman/daire-detay')));
const KisiListesiPage = Loadable(lazy(() => import('pages/apartman/kisi-listesi')));
const ProfilPage = Loadable(lazy(() => import('pages/profil')));
const OdemelerPage = Loadable(lazy(() => import('pages/odemeler')));
const ApartmanFinansalDurumPage = Loadable(lazy(() => import('pages/apartman-finansal-durum')));
const DuyurularPage = Loadable(lazy(() => import('pages/duyurular')));
const AnketlerPage = Loadable(lazy(() => import('pages/anketler')));
const MesajPage = Loadable(lazy(() => import('pages/iletisim/mesaj')));
const AcilDurumPage = Loadable(lazy(() => import('pages/iletisim/acil-durum')));
const KararDefteriPage = Loadable(lazy(() => import('pages/karar-defteri')));
const DilekOneriSikayetPage = Loadable(lazy(() => import('pages/dilek-oneri-sikayet')));
const ArizaBildirimiPage = Loadable(lazy(() => import('pages/ariza-bildirimi')));
const GelirIslemleriPage = Loadable(lazy(() => import('pages/finans/gelir-islemleri')));
const GiderIslemleriPage = Loadable(lazy(() => import('pages/finans/gider-islemleri')));
const TahsilatIslemleriPage = Loadable(lazy(() => import('pages/finans/tahsilat-islemleri')));
const OdemeIslemleriPage = Loadable(lazy(() => import('pages/finans/odeme-islemleri')));
const AlacakBorcYonetimiPage = Loadable(lazy(() => import('pages/finans/alacak-borc-yonetimi')));
const AidatIslemleriPage = Loadable(lazy(() => import('pages/finans/aidat-islemleri')));
const OtomatikAlacakBorcPage = Loadable(lazy(() => import('pages/finans/otomatik-alacak-borc')));
const TopluIslemlerPage = Loadable(lazy(() => import('pages/finans/toplu-islemler')));
const SayacIslemleriPage = Loadable(lazy(() => import('pages/finans/sayac-islemleri')));
const KasalarArasiTransferPage = Loadable(lazy(() => import('pages/finans/kasalar-arasi-transfer')));
const YonetimTopluIslemlerPage = Loadable(lazy(() => import('pages/yonetim/toplu-islemler')));
const ButcePlanlamaPage = Loadable(lazy(() => import('pages/yonetim/butce-planlama')));
const VukuatPage = Loadable(lazy(() => import('pages/yonetim/vukuat')));
const TeklifYonetimiPage = Loadable(lazy(() => import('pages/yonetim/teklif-yonetimi')));
const ToplantiYonetimiPage = Loadable(lazy(() => import('pages/yonetim/toplanti-yonetimi')));
const DemirbasPage = Loadable(lazy(() => import('pages/yonetim/demirbas')));
const StokPage = Loadable(lazy(() => import('pages/yonetim/stok')));
const EvrakArsiviPage = Loadable(lazy(() => import('pages/yonetim/evrak-arsivi')));
const CalisanYonetimiPage = Loadable(lazy(() => import('pages/personel/calisan-yonetimi')));
const OnlineOdemeAyarlariPage = Loadable(lazy(() => import('pages/ayarlar/online-odeme-ayarlari')));
const BankaHareketleriEslestirmePage = Loadable(lazy(() => import('pages/ayarlar/banka-hareketleri-eslestirme')));
const ComingSoon = Loadable(lazy(() => import('pages/ComingSoon')));

// Modül 10 (Raporlamalar): 24 rapor tek bir genel bileşeni (RaporSayfasi)
// RAPOR_KATALOGU'ndaki `tanim`iyle örnekleyerek paylaşır — 24 ayrı sayfa
// dosyası yerine tek lazy import + katalogdan üretilen REAL_PAGES girdileri.
const RaporSayfasiPage = Loadable(lazy(() => import('pages/reports/RaporSayfasi')));
const RAPOR_SAYFALARI = Object.fromEntries(RAPOR_KATALOGU.map((tanim) => [tanim.route, () => <RaporSayfasiPage tanim={tanim} />]));

// gerçek sayfası bu fazda yazılan route'lar; listede olmayan her routableItem
// (placeholder olanlar dahil) ComingSoon'a düşer — bkz. menu-items/config.jsx.
const REAL_PAGES = {
  ...RAPOR_SAYFALARI,
  '/': DashboardPage,
  '/bildirimler': BildirimlerPage,
  '/notlar': NotesPage,
  '/apartman/daire-listesi': DaireListesiPage,
  '/apartman/kisi-listesi': KisiListesiPage,
  '/profil': ProfilPage,
  '/odemeler': OdemelerPage,
  '/apartman-finansal-durum': ApartmanFinansalDurumPage,
  '/iletisim/duyuru': DuyurularPage,
  '/iletisim/anket': AnketlerPage,
  '/iletisim/mesaj': MesajPage,
  '/iletisim/acil-durum': AcilDurumPage,
  '/yonetim/karar-defteri': KararDefteriPage,
  '/basvurularim/dilek-oneri-sikayet': DilekOneriSikayetPage,
  '/basvurularim/ariza-bildirimi': ArizaBildirimiPage,
  '/finans/gelir-islemleri': GelirIslemleriPage,
  '/finans/gider-islemleri': GiderIslemleriPage,
  '/finans/tahsilat-islemleri': TahsilatIslemleriPage,
  '/finans/odeme-islemleri': OdemeIslemleriPage,
  '/finans/alacak-borc-yonetimi': AlacakBorcYonetimiPage,
  '/finans/aidat-islemleri': AidatIslemleriPage,
  '/finans/otomatik-alacak-borc': OtomatikAlacakBorcPage,
  '/finans/toplu-islemler': TopluIslemlerPage,
  '/finans/sayac-islemleri': SayacIslemleriPage,
  '/finans/kasalar-arasi-transfer': KasalarArasiTransferPage,
  '/yonetim/toplu-islemler': YonetimTopluIslemlerPage,
  '/yonetim/butce-planlama': ButcePlanlamaPage,
  '/yonetim/vukuat': VukuatPage,
  '/yonetim/teklif-yonetimi': TeklifYonetimiPage,
  '/yonetim/toplanti-yonetimi': ToplantiYonetimiPage,
  '/yonetim/demirbas': DemirbasPage,
  '/yonetim/stok': StokPage,
  '/yonetim/evrak-arsivi': EvrakArsiviPage,
  '/personel/calisan-yonetimi': CalisanYonetimiPage,
  '/ayarlar/online-odeme-ayarlari': OnlineOdemeAyarlariPage,
  '/ayarlar/banka-hareketleri-eslestirme': BankaHareketleriEslestirmePage
};

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <DashboardLayout />
    </AuthGuard>
  ),
  children: [
    { path: ZORUNLU_PAROLA_DEGISTIR_PATH, element: <ZorunluParolaDegistirPage /> },
    {
      // Daire Listesi tablosundaki "Detay" aksiyonundan açılır; menüde ayrı bir
      // girdisi olmadığı için routableItems akışının dışında elle eklenir.
      // "Daire Listesi / Tanımlama" linkinin altına (prefix) nest edilir ki
      // sidebar'da bu sayfadayken doğru menü öğesi aktif görünmeye devam etsin.
      path: 'apartman/daire-listesi/:id',
      element: (
        <RoleGuard allow={[YONETICI, BINA_SAKINI]}>
          <DaireDetayPage />
        </RoleGuard>
      )
    },
    ...routableItems.map((item) => {
      const Page = REAL_PAGES[item.url] || ComingSoon;
      return {
        path: item.url,
        element: (
          <RoleGuard allow={item.roles}>
            <Page />
          </RoleGuard>
        )
      };
    })
  ]
};

export default MainRoutes;
